/**
 * Client HTTP tipizzato verso l'API Core.
 *
 * Scritto a mano da `docs/CONVENTIONS.md` §5 e §6: il contratto e' il documento,
 * non lo schema OpenAPI generato a runtime. Ogni funzione esportata corrisponde
 * a **un endpoint documentato**; dove il documento non prevede un endpoint, il
 * back-office deriva il dato dai dati che l'API espone gia' (vedi
 * `src/lib/cruscotto.ts` e `src/lib/scadenze.ts`) invece di inventarne uno.
 *
 * Tre invarianti di questo modulo:
 *
 * 1. **Nessuna formattazione.** Date, importi e pesi viaggiano gia' come stringhe
 *    italiane (CONVENTIONS §2.3): il client non converte, non arrotonda, non usa
 *    `toLocaleString`. Le sole trasformazioni ammesse sono quelle inverse, sui
 *    dati che l'utente digita, e stanno in `src/lib/formats.ts`.
 * 2. **Un solo tipo di errore.** Qualunque risposta non riuscita diventa un
 *    `ErroreApi` con `codice` stabile e `messaggio` gia' in italiano, cosi' come
 *    l'API lo produce. Le pagine non leggono mai `response.status`.
 * 3. **Il token non si passa a mano.** Lo legge la custodia di `session.ts`.
 */

import { messaggioConDettagli } from "./messaggi";
import {
  apriSessione,
  chiudiSessione,
  tokenCorrente,
} from "./session";
import type {
  AtMap,
  Cartella,
  CorpoErrore,
  DataIt,
  DettaglioErrore,
  Ente,
  IdNodo,
  Nodo,
  Pubblicazione,
  RichiestaAccesso,
  RichiestaAggiornamentoCartella,
  RichiestaAggiornamentoNodo,
  RichiestaAggiornamentoUtente,
  RichiestaCreazioneCartella,
  RichiestaCreazioneNodo,
  RichiestaCopia,
  RichiestaCreazionePubblicazione,
  RichiestaInvitoUtente,
  RispostaAccesso,
  RispostaAlberatura,
  RispostaAssegnazioni,
  RispostaElencoPubblicazioni,
  RispostaPubblicazioneEseguita,
  RispostaPubblicazioniNodo,
  SchemaAnac,
  RegolaObbligo,
  RichiestaRegola,
  RichiestaValutazione,
  RispostaValutazioni,
  StatoPubblicazione,
  Utente,
  UtenteCorrente,
  Valutazione,
  Documento,
  ElencoDocumenti,
  FormatoDocumento,
  StatoAccessibilita,
  AperturaAssistente,
  RichiestaChatAssistente,
  StatoAssistente,
} from "./types";

// --------------------------------------------------------------------------- //
// Configurazione
// --------------------------------------------------------------------------- //

/**
 * Radice dell'API. Vuota in sviluppo: `vite.config.ts` inoltra `/api` e
 * `/at_map.json` all'API Core, cosi' il browser non fa richieste cross-origin e
 * non serve alcun permesso CORS aggiuntivo.
 */
const RADICE_API: string = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");

/**
 * Radice pubblica del portale (`PORTALE_BASE_URL`), se nota al back-office.
 *
 * Serve solo alla verifica «da crawler» del pannello di conformita': la at_map
 * che TrasparenzAI legge e' quella servita dal dominio del Comune, non quella
 * dell'API. Vuota se non configurata: in quel caso la verifica non si esegue
 * invece di dare un esito falsamente rassicurante.
 *
 * `trim` prima di tutto: con uno spazio in coda al valore — capitato davvero,
 * nella variabile di repository da cui la CI lo prende — la richiesta andava a
 * `https://dominio.it /at_map.json`, che non e' un URL, e il pannello dichiarava
 * irraggiungibile un portale che rispondeva.
 */
const RADICE_PORTALE: string = (import.meta.env.VITE_PORTALE_BASE_URL ?? "")
  .trim()
  .replace(/\/+$/, "");

/** Prefisso applicativo versionato — `settings.api_v1_prefix`. */
const PREFISSO_V1 = "/api/v1";

/** Millisecondi oltre i quali una richiesta viene abbandonata. */
const TIMEOUT_MS = 30_000;

// --------------------------------------------------------------------------- //
// Errore
// --------------------------------------------------------------------------- //

/**
 * Errore normalizzato dell'API.
 *
 * `messaggio` e' gia' in italiano e mostrabile all'utente; `codice` e' la
 * costante stabile su cui si ramifica il comportamento del client.
 */
export class ErroreApi extends Error {
  readonly codice: string;
  readonly dettagli: DettaglioErrore[];
  readonly stato: number;

  constructor(codice: string, messaggio: string, stato: number, dettagli: DettaglioErrore[] = []) {
    super(messaggio);
    this.name = "ErroreApi";
    this.codice = codice;
    this.dettagli = dettagli;
    this.stato = stato;
  }

  /** Vero se la risorsa richiesta non esiste. */
  get nonTrovato(): boolean {
    return this.codice === "NON_TROVATO" || this.stato === 404;
  }

  /** Vero se il perimetro dell'utente non copre l'operazione richiesta. */
  get nonAutorizzato(): boolean {
    return this.codice === "NON_AUTORIZZATO" || this.stato === 403;
  }

  /** Messaggio pronto per la UI, con le violazioni di campo accodate. */
  get messaggioEsteso(): string {
    return messaggioConDettagli(this.message, this.dettagli);
  }
}

/** Messaggi di fallback quando l'errore non arriva dall'API in forma nota. */
const MESSAGGI_RETE = {
  interrotta: "La richiesta ha impiegato troppo tempo ed e' stata interrotta.",
  irraggiungibile:
    "Non e' stato possibile contattare il servizio. Verificare la connessione e che l'API Core sia in esecuzione.",
  illeggibile: "Il servizio ha risposto in un formato non previsto.",
} as const;

/** Traduce il corpo di errore dell'API in un `ErroreApi`, con fallback prudenti. */
async function erroreDaRisposta(risposta: Response): Promise<ErroreApi> {
  let corpo: unknown = null;
  try {
    corpo = await risposta.json();
  } catch {
    corpo = null;
  }

  const busta = corpo as Partial<CorpoErrore> | null;
  if (busta?.errore?.codice && busta.errore.messaggio) {
    return new ErroreApi(
      busta.errore.codice,
      busta.errore.messaggio,
      risposta.status,
      busta.errore.dettagli ?? [],
    );
  }
  return new ErroreApi(
    "ERRORE_INTERNO",
    `${MESSAGGI_RETE.illeggibile} (HTTP ${risposta.status})`,
    risposta.status,
  );
}

// --------------------------------------------------------------------------- //
// Motore delle richieste
// --------------------------------------------------------------------------- //

type Metodo = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * Valori ammessi in querystring; `undefined`, `null` e `""` non vengono inviati.
 *
 * Un array diventa **la stessa chiave ripetuta** (`?accessibilita=presunta&
 * accessibilita=non_garantita`), che e' la forma attesa da un parametro
 * dichiarato `list[...]` in FastAPI. Serializzarlo come lista separata da
 * virgole darebbe un 422, e usare `set` invece di `append` manderebbe l'ultimo
 * valore soltanto — cioe' un filtro multiplo che ne applica uno.
 */
type ValoreQuery = string | number | boolean | null | undefined;
type ParametriQuery = Record<string, ValoreQuery | readonly ValoreQuery[]>;

interface OpzioniRichiesta {
  /** Corpo JSON. Ignorato se `modulo` e' valorizzato. */
  corpo?: unknown;
  /** Corpo `multipart/form-data`, per gli upload. */
  modulo?: FormData;
  query?: ParametriQuery;
  /** Se falso la richiesta parte senza header `Authorization` (endpoint pubblici). */
  autenticata?: boolean;
  /**
   * Vero per le letture di rotte pubbliche che il server mette in cache
   * (`Cache-Control: public, max-age=300`): il browser le richiede comunque al
   * server invece di ripresentare la copia che ha.
   *
   * Il back-office deve vedere lo stato di adesso: senza, dopo uno spostamento
   * o una pubblicazione la tabella mostrava per cinque minuti la risposta di
   * prima, e il documento sembrava non essersi mosso. Vale solo dove serve: le
   * rotte del back-office non mandano quell'intestazione, e togliere la cache a
   * tutte le richieste non ne correggeva nessuna in piu'.
   */
  aggiornata?: boolean;
  segnale?: AbortSignal;
}

function componiUrl(percorso: string, query?: ParametriQuery): string {
  const base = `${RADICE_API}${percorso}`;
  if (!query) return base;
  const parametri = new URLSearchParams();
  for (const [chiave, valore] of Object.entries(query)) {
    if (valore === undefined || valore === null || valore === "") continue;
    if (Array.isArray(valore)) {
      for (const voce of valore) {
        if (voce === undefined || voce === null || voce === "") continue;
        parametri.append(chiave, String(voce));
      }
      continue;
    }
    parametri.set(chiave, String(valore));
  }
  const stringa = parametri.toString();
  return stringa ? `${base}?${stringa}` : base;
}

/**
 * Esegue una richiesta e restituisce il corpo deserializzato.
 *
 * Su 401 chiude la sessione: le pagine non devono gestire il caso: il
 * `ProtectedRoute` reagisce alla decadenza e riporta all'accesso.
 */
async function richiesta<T>(
  metodo: Metodo,
  percorso: string,
  opzioni: OpzioniRichiesta = {},
): Promise<T> {
  const { corpo, modulo, query, autenticata = true, aggiornata = false, segnale } = opzioni;

  const intestazioni = new Headers({ Accept: "application/json" });
  if (autenticata) {
    const token = tokenCorrente();
    if (token) intestazioni.set("Authorization", `Bearer ${token}`);
  }

  let payload: BodyInit | undefined;
  if (modulo) {
    // Il boundary lo imposta il browser: non si tocca Content-Type.
    payload = modulo;
  } else if (corpo !== undefined) {
    intestazioni.set("Content-Type", "application/json");
    payload = JSON.stringify(corpo);
  }

  const orologio = new AbortController();
  const scadenza = window.setTimeout(() => orologio.abort(), TIMEOUT_MS);
  if (segnale) {
    if (segnale.aborted) orologio.abort();
    else segnale.addEventListener("abort", () => orologio.abort(), { once: true });
  }

  let risposta: Response;
  try {
    risposta = await fetch(componiUrl(percorso, query), {
      method: metodo,
      headers: intestazioni,
      body: payload,
      signal: orologio.signal,
      credentials: "omit",
      // `no-cache` e non `no-store`: si chiede al server ogni volta, ma la
      // risposta resta una normale risposta HTTP. Vedi `aggiornata`.
      cache: aggiornata ? "no-cache" : "default",
    });
  } catch (errore) {
    if (segnale?.aborted) throw errore;
    const interrotta = errore instanceof DOMException && errore.name === "AbortError";
    throw new ErroreApi(
      "ERRORE_INTERNO",
      interrotta ? MESSAGGI_RETE.interrotta : MESSAGGI_RETE.irraggiungibile,
      0,
    );
  } finally {
    window.clearTimeout(scadenza);
  }

  if (risposta.status === 401) {
    chiudiSessione();
    throw await erroreDaRisposta(risposta);
  }
  if (!risposta.ok) {
    throw await erroreDaRisposta(risposta);
  }
  if (risposta.status === 204 || risposta.headers.get("Content-Length") === "0") {
    return undefined as T;
  }

  try {
    return (await risposta.json()) as T;
  } catch {
    throw new ErroreApi("ERRORE_INTERNO", MESSAGGI_RETE.illeggibile, risposta.status);
  }
}

// --------------------------------------------------------------------------- //
// Autenticazione — POST /auth/login, GET /auth/me
// --------------------------------------------------------------------------- //

/** Apre la sessione e ne registra il token nella custodia. */
export async function accedi(credenziali: RichiestaAccesso): Promise<RispostaAccesso> {
  const risposta = await richiesta<RispostaAccesso>("POST", `${PREFISSO_V1}/auth/login`, {
    corpo: credenziali,
    autenticata: false,
  });
  apriSessione(risposta);
  return risposta;
}

/**
 * Profilo e perimetro dell'utente corrente.
 *
 * E' anche la verifica del token ripristinato da `localStorage`: se fallisce con
 * 401 la custodia ha gia' chiuso la sessione.
 */
export function profiloCorrente(segnale?: AbortSignal): Promise<UtenteCorrente> {
  return richiesta<UtenteCorrente>("GET", `${PREFISSO_V1}/auth/me`, { segnale });
}

/** Chiude la sessione lato client. */
export function esci(): void {
  chiudiSessione();
}

// --------------------------------------------------------------------------- //
// Alberatura — GET /nodi, POST/PUT/DELETE /nodi (scrittura: solo super admin)
// --------------------------------------------------------------------------- //

/** Alberatura completa dell'ente, gia' annidata sui tre livelli ANAC c1. */
export function leggiAlberatura(
  enteId?: number | null,
  segnale?: AbortSignal,
): Promise<RispostaAlberatura> {
  return richiesta<RispostaAlberatura>("GET", `${PREFISSO_V1}/nodi`, {
    query: { ente: enteId ?? undefined },
    segnale,
  });
}

/** Singolo nodo dell'alberatura. */
export function leggiNodo(idNodo: IdNodo, segnale?: AbortSignal): Promise<Nodo> {
  return richiesta<Nodo>("GET", `${PREFISSO_V1}/nodi/${encodeURIComponent(idNodo)}`, { segnale });
}

/** Crea un nodo. L'API rifiuta la richiesta se il chiamante non e' super admin. */
export function creaNodo(dati: RichiestaCreazioneNodo): Promise<Nodo> {
  return richiesta<Nodo>("POST", `${PREFISSO_V1}/nodi`, { corpo: dati });
}

/**
 * Aggiorna un nodo: applicabilita', denominazione da catalogo, ordine.
 *
 * `ente` e' **obbligatorio** in querystring: a differenza delle letture
 * pubbliche (CONVENTIONS §9.1, dove l'ente omesso ricade sul primo attivo) le
 * rotte di scrittura dell'alberatura esigono di sapere su quale ente si sta
 * intervenendo, perche' un'assunzione implicita modificherebbe l'alberatura
 * dell'ente sbagliato.
 */
export function aggiornaNodo(
  idNodo: IdNodo,
  dati: RichiestaAggiornamentoNodo,
  enteId: number,
): Promise<Nodo> {
  return richiesta<Nodo>("PUT", `${PREFISSO_V1}/nodi/${encodeURIComponent(idNodo)}`, {
    corpo: dati,
    query: { ente: enteId },
  });
}

/**
 * Rimuove un nodo senza figli — una sottosezione o un obbligo, cioe' il secondo
 * o il terzo livello. Una sezione non si rimuove: le 22 sono l'albero stesso.
 * `ente` e' obbligatorio, come su `aggiornaNodo`.
 *
 * Su una voce **a catalogo ANAC** l'API risponde 409 `RIMOZIONE_NODO_A_CATALOGO`
 * e insegna la via giusta: dichiarare il nodo non applicabile, cosi' resta
 * nell'alberatura con la dicitura prevista invece di sparire dalla at_map.
 * `confermaRimozioneCatalogo` e' la via d'uscita per il caso opposto — un nodo
 * creato per errore su un ente non ancora in linea — e va passata solo dopo che
 * l'operatore ha letto quel messaggio: e' una dichiarazione, non un ritenta.
 */
export function eliminaNodo(
  idNodo: IdNodo,
  enteId: number,
  confermaRimozioneCatalogo = false,
): Promise<void> {
  return richiesta<void>("DELETE", `${PREFISSO_V1}/nodi/${encodeURIComponent(idNodo)}`, {
    query: confermaRimozioneCatalogo
      ? { ente: enteId, conferma_rimozione_catalogo: true }
      : { ente: enteId },
  });
}

// --------------------------------------------------------------------------- //
// Pubblicazioni — GET/POST /pubblicazioni, POST /{id}/documenti, /{id}/pubblica
// --------------------------------------------------------------------------- //

export interface FiltriPubblicazioni {
  nodo?: IdNodo;
  anno?: number;
  stato?: StatoPubblicazione;
  /**
   * Restringe a un ente. L'API lo onora **solo per il Super Admin**, che e'
   * l'unico a vedere oltre il proprio: per un redattore l'elenco e' gia' quello
   * del suo ente, e passarlo o ometterlo non cambia nulla.
   */
  ente?: number;
}

/**
 * Elenco delle pubblicazioni nel perimetro dell'utente.
 *
 * I filtri viaggiano in querystring; l'elenco viene comunque rifiltrato lato
 * client in `Pubblicazioni.tsx`, cosi' la pagina resta corretta anche se
 * l'implementazione del router ignora un parametro.
 */
export async function elencaPubblicazioni(
  filtri: FiltriPubblicazioni = {},
  segnale?: AbortSignal,
): Promise<Pubblicazione[]> {
  const risposta = await richiesta<RispostaElencoPubblicazioni | Pubblicazione[]>(
    "GET",
    `${PREFISSO_V1}/pubblicazioni`,
    {
      query: {
        nodo: filtri.nodo,
        anno: filtri.anno,
        stato: filtri.stato,
        ente: filtri.ente,
      },
      segnale,
    },
  );
  return Array.isArray(risposta) ? risposta : risposta.pubblicazioni;
}

/** Pubblicazioni di un nodo, con gli anni disponibili per il filtro. */
export function leggiPubblicazioniNodo(
  idNodo: IdNodo,
  anno?: number,
  segnale?: AbortSignal,
  enteId?: number | null,
  archiviate = false,
): Promise<RispostaPubblicazioniNodo> {
  // `ente` va passato: la rotta e' pubblica e, senza, serve il primo ente
  // dell'installazione. Il super admin che lavora su un altro ente vedeva i
  // documenti di quello e le cartelle del suo. La rotta e' anche in cache per
  // il portale: da qui la si legge `aggiornata`.
  return richiesta<RispostaPubblicazioniNodo>(
    "GET",
    `${PREFISSO_V1}/nodi/${encodeURIComponent(idNodo)}/pubblicazioni`,
    {
      query: { anno, ente: enteId ?? undefined, archiviate: archiviate || undefined },
      aggiornata: true,
      segnale,
    },
  );
}

/** Crea una bozza. Nessun effetto sul portale pubblico finche' non si pubblica. */
export function creaPubblicazione(dati: RichiestaCreazionePubblicazione): Promise<Pubblicazione> {
  return richiesta<Pubblicazione>("POST", `${PREFISSO_V1}/pubblicazioni`, { corpo: dati });
}

/**
 * Carica un documento su una bozza.
 *
 * Il nome del file lo genera il sistema (`<Obbligo>-YYYYMMDD-YYYYMMDD.ext`): qui
 * si inviano solo il contenuto e il titolo mostrato nel link pubblico.
 */
export function caricaDocumento(
  idPubblicazione: number,
  file: File,
  titolo: string,
): Promise<Documento> {
  const modulo = new FormData();
  modulo.append("file", file);
  modulo.append("titolo", titolo);
  return richiesta<Documento>(
    "POST",
    `${PREFISSO_V1}/pubblicazioni/${idPubblicazione}/documenti`,
    { modulo },
  );
}

/**
 * Toglie un documento dalla **versione in lavorazione** di una bozza.
 *
 * Non e' una cancellazione di contenuto pubblicato: cio' che e' stato pubblicato
 * resta al proprio indirizzo (CONVENTIONS §4 e §7.1) e l'API risponde `CONFLITTO`
 * se il documento appartiene a una versione gia' pubblicata. Serve a sostituire
 * un file caricato per errore **prima** della pubblicazione: senza questa
 * chiamata il file scartato resterebbe appeso alla bozza e verrebbe pubblicato
 * insieme a quello corretto, con il discriminante `-2` di §9.6.
 */
export function eliminaDocumento(
  idPubblicazione: number,
  idDocumento: number,
): Promise<void> {
  return richiesta<void>(
    "DELETE",
    `${PREFISSO_V1}/pubblicazioni/${idPubblicazione}/documenti/${idDocumento}`,
  );
}

/** Rilegge una pubblicazione: serve a seguire l'esito delle validazioni asincrone. */
export function leggiPubblicazione(
  idPubblicazione: number,
  segnale?: AbortSignal,
): Promise<Pubblicazione> {
  return richiesta<Pubblicazione>("GET", `${PREFISSO_V1}/pubblicazioni/${idPubblicazione}`, {
    segnale,
  });
}

/**
 * Transizione bozza → pubblicato.
 *
 * Lato server crea una versione immutabile, sposta l'alias `-latest` e rigenera
 * la at_map: e' l'unico punto in cui il contenuto diventa pubblico.
 */
export function pubblica(idPubblicazione: number): Promise<RispostaPubblicazioneEseguita> {
  return richiesta<RispostaPubblicazioneEseguita>(
    "POST",
    `${PREFISSO_V1}/pubblicazioni/${idPubblicazione}/pubblica`,
  );
}

/**
 * Archivia una pubblicazione con il quinquennio maturo.
 *
 * L'endpoint esisteva e nessuno lo chiamava. Serve perche' il worker di
 * archiviazione **non e' schedulato** su questa installazione (vedi
 * `docs/DECISIONI-APERTE.md` §12): finche' non lo sara', l'unico esecutore
 * dell'art. 8 c. 3 e' una persona, e nasconderle il comando non rende
 * l'archiviazione automatica — la rende soltanto invisibile.
 *
 * Non cancella: il contenuto esce dalla navigazione corrente e resta
 * accessibile ai suoi indirizzi.
 */
export function archivia(idPubblicazione: number): Promise<Pubblicazione> {
  return richiesta<Pubblicazione>(
    "POST",
    `${PREFISSO_V1}/pubblicazioni/${idPubblicazione}/archivia`,
  );
}

// --------------------------------------------------------------------------- //
// Valutazioni delle allerte
//
// Il giornale delle decisioni prese sui contenuti scaduti. **Non esiste un
// endpoint che elenchi le allerte**: quelle le calcola `lib/allerte.ts`, perche'
// dipendono dalla periodicita' degli obblighi, che l'API non possiede. Di qui
// passa solo cio' che si registra.
// --------------------------------------------------------------------------- //

export interface FiltriValutazioni {
  nodo?: IdNodo;
  tipo?: string;
  /** Solo i verdetti che stanno ancora spegnendo la propria allerta. */
  attive?: boolean;
  ente?: number;
}

/** Giornale delle decisioni visibili all'utente, dalla piu' recente. */
export async function elencaValutazioni(
  filtri: FiltriValutazioni = {},
  segnale?: AbortSignal,
): Promise<Valutazione[]> {
  const risposta = await richiesta<RispostaValutazioni | Valutazione[]>(
    "GET",
    `${PREFISSO_V1}/valutazioni`,
    {
      query: {
        nodo: filtri.nodo,
        tipo: filtri.tipo,
        attive: filtri.attive ? true : undefined,
        ente: filtri.ente,
      },
      segnale,
    },
  );
  return Array.isArray(risposta) ? risposta : risposta.valutazioni;
}

/** Registra un verdetto. Il perimetro lo verifica l'API, non questo client. */
export function registraValutazione(
  dati: RichiestaValutazione,
  enteId?: number | null,
): Promise<Valutazione> {
  return richiesta<Valutazione>("POST", `${PREFISSO_V1}/valutazioni`, {
    corpo: dati,
    query: { ente: enteId ?? undefined },
  });
}

/**
 * Revoca un verdetto senza cancellarlo.
 *
 * Non esiste un aggiornamento: un ripensamento e' questa revoca piu' un
 * verdetto nuovo, e restano entrambi. E' cio' che rende il giornale una prova.
 */
export function revocaValutazione(
  idValutazione: number,
  motivo: string,
): Promise<Valutazione> {
  return richiesta<Valutazione>(
    "POST",
    `${PREFISSO_V1}/valutazioni/${idValutazione}/revoca`,
    { corpo: { motivo } },
  );
}

/**
 * Tutte le nature dichiarate, in una richiesta sola.
 *
 * E' cio' che serve al calcolo delle allerte, che le vuole **tutte insieme**.
 * Le alternative erano peggiori: 121 letture per nodo, oppure ricostruire lo
 * stato corrente dal giornale lato client — e quella l'abbiamo provata, con il
 * risultato che vinceva la dichiarazione piu' vecchia e correggere una regola
 * non aveva effetto.
 */
export async function elencaRegole(
  enteId?: number | null,
  segnale?: AbortSignal,
): Promise<RegolaObbligo[]> {
  const risposta = await richiesta<{ regole: RegolaObbligo[] } | RegolaObbligo[]>(
    "GET",
    `${PREFISSO_V1}/valutazioni/regole`,
    { query: { ente: enteId ?? undefined }, segnale },
  );
  return Array.isArray(risposta) ? risposta : risposta.regole;
}

/**
 * Natura dichiarata di un obbligo, o `null` se il Comune non l'ha dichiarata.
 *
 * L'assenza non e' un errore: significa «vale l'ipotesi ereditata dalla
 * sezione», ed e' lo stato di partenza di tutti i 121 obblighi. Per questo il
 * 404 si traduce in `null` invece di propagarsi.
 */
export async function leggiRegola(
  idNodo: IdNodo,
  enteId?: number | null,
  segnale?: AbortSignal,
): Promise<RegolaObbligo | null> {
  try {
    return await richiesta<RegolaObbligo>(
      "GET",
      `${PREFISSO_V1}/nodi/${encodeURIComponent(idNodo)}/regola`,
      { query: { ente: enteId ?? undefined }, segnale },
    );
  } catch (causa) {
    if (causa instanceof ErroreApi && causa.nonTrovato) return null;
    throw causa;
  }
}

/** Dichiara la natura di un obbligo. L'API la riserva al Super Admin. */
export function dichiaraRegola(
  idNodo: IdNodo,
  dati: RichiestaRegola,
  enteId?: number | null,
): Promise<RegolaObbligo> {
  return richiesta<RegolaObbligo>(
    "PUT",
    `${PREFISSO_V1}/nodi/${encodeURIComponent(idNodo)}/regola`,
    { corpo: dati, query: { ente: enteId ?? undefined } },
  );
}

// --------------------------------------------------------------------------- //
// Configurazione — solo super admin
// --------------------------------------------------------------------------- //

/** Elenco degli utenti visibili al super admin. */
export async function elencaUtenti(segnale?: AbortSignal): Promise<Utente[]> {
  const risposta = await richiesta<{ utenti: Utente[] } | Utente[]>(
    "GET",
    `${PREFISSO_V1}/utenti`,
    { segnale },
  );
  return Array.isArray(risposta) ? risposta : risposta.utenti;
}

/** Invita un redattore e ne fissa il perimetro iniziale di nodi. */
export function invitaUtente(dati: RichiestaInvitoUtente): Promise<Utente> {
  return richiesta<Utente>("POST", `${PREFISSO_V1}/utenti`, { corpo: dati });
}

/** Aggiorna i dati anagrafici di un utente: nome utente, email, ruolo, ente, stato. */
export function aggiornaUtente(
  idUtente: number,
  dati: RichiestaAggiornamentoUtente,
): Promise<Utente> {
  return richiesta<Utente>("PUT", `${PREFISSO_V1}/utenti/${idUtente}`, { corpo: dati });
}

/**
 * Rilascia un token per operare un'ora nei panni di un'altra utenza.
 *
 * L'API la riserva al Super Admin e la rifiuta su un altro Super Admin, su se
 * stessi e su un'utenza sospesa. Il token che torna porta i poteri
 * **dell'utenza impersonificata**, non quelli di chi lo ha chiesto: la
 * configurazione resta chiusa.
 *
 * Da sapere: cio' che si scrive mentre e' attiva risulta dell'utenza
 * impersonificata, senza alcuna menzione dell'amministratore.
 */
export function impersonifica(idUtente: number): Promise<RispostaAccesso> {
  return richiesta<RispostaAccesso>(
    "POST",
    `${PREFISSO_V1}/utenti/${idUtente}/impersonifica`,
  );
}

/**
 * Sostituisce il perimetro di nodi di un'utenza.
 *
 * Endpoint dedicato: `PUT /api/v1/utenti/{id}` **non** accetta `nodi_assegnati`.
 * L'elenco e' **sostitutivo** — descrive il perimetro completo dopo
 * l'operazione — perche' in sede di audit la domanda e' «su cosa puo' operare
 * oggi questa utenza?», e con aggiornamenti incrementali la risposta
 * dipenderebbe dalla storia delle chiamate.
 */
export function assegnaNodi(
  idUtente: number,
  nodi: IdNodo[],
): Promise<RispostaAssegnazioni> {
  return richiesta<RispostaAssegnazioni>("PUT", `${PREFISSO_V1}/utenti/${idUtente}/nodi`, {
    corpo: { nodi },
  });
}

/** Elenco degli enti (multi-tenant). */
export async function elencaEnti(segnale?: AbortSignal): Promise<Ente[]> {
  const risposta = await richiesta<{ enti: Ente[] } | Ente[]>("GET", `${PREFISSO_V1}/enti`, {
    segnale,
  });
  return Array.isArray(risposta) ? risposta : risposta.enti;
}

/** Schemi ANAC configurati, con la relativa data di decorrenza. */
export async function elencaSchemi(segnale?: AbortSignal): Promise<SchemaAnac[]> {
  const risposta = await richiesta<{ schemi: SchemaAnac[] } | SchemaAnac[]>(
    "GET",
    `${PREFISSO_V1}/schemi`,
    { segnale },
  );
  return Array.isArray(risposta) ? risposta : risposta.schemi;
}

// --------------------------------------------------------------------------- //
// at_map — endpoint pubblico a URL fisso
// --------------------------------------------------------------------------- //

/**
 * Legge `GET /at_map.json` sull'API, la mappa id_nodo → URL.
 *
 * E' pubblica e va letta **senza** header `Authorization`. `ente` va passato in
 * un'installazione multi-ente: omesso, l'API ricade sul primo ente attivo
 * (CONVENTIONS §9.1) e il confronto con l'alberatura mostrata a schermo
 * segnalerebbe come divergente ogni nodo di un ente diverso dal primo.
 *
 * Si legge `aggiornata`: la rotta e' in cache cinque minuti, e il pannello di
 * conformita' si apre proprio dopo una pubblicazione, per vedere la mappa
 * rigenerata.
 */
export function leggiAtMap(enteId?: number | null, segnale?: AbortSignal): Promise<AtMap> {
  return richiesta<AtMap>("GET", "/at_map.json", {
    autenticata: false,
    aggiornata: true,
    query: { ente: enteId ?? undefined },
    segnale,
  });
}

/**
 * Legge la at_map **all'indirizzo pubblico del portale**, cioe' quella che
 * TrasparenzAI scarica davvero.
 *
 * L'API e il portale servono due mappe distinte: quella dell'API e' generata dal
 * modello dati, quella del portale e' cio' che il crawler trova sul dominio del
 * Comune (CONVENTIONS §4). Verificare solo la prima puo' dichiarare conforme una
 * situazione in cui la mappa pubblica e' un ripiego sulle fixture.
 *
 * Torna `null` se l'indirizzo del portale non e' configurato o non risponde: e'
 * un esito da distinguere in pannello, non un errore da propagare.
 */
export async function leggiAtMapPortale(segnale?: AbortSignal): Promise<AtMap | null> {
  if (!RADICE_PORTALE) return null;
  try {
    const risposta = await fetch(`${RADICE_PORTALE}/at_map.json`, {
      headers: { Accept: "application/json" },
      credentials: "omit",
      signal: segnale,
    });
    if (!risposta.ok) return null;
    return (await risposta.json()) as AtMap;
  } catch {
    return null;
  }
}

/** Indirizzo pubblico del portale, se configurato: serve alla verifica «da crawler». */
export function indirizzoPortale(): string | null {
  return RADICE_PORTALE || null;
}

// --------------------------------------------------------------------------- //
// Cartelle documentali
//
// Organizzare i documenti e' un'operazione separata dal pubblicarli: queste
// chiamate non creano versioni, non toccano nomi di file e non spostano alias.
// E' la ragione per cui `spostaDocumento` e' ammessa anche su un documento gia'
// pubblicato — l'URL versionato e l'alias `-latest` restano quelli, e chi aveva
// salvato l'indirizzo lo ritrova.
// --------------------------------------------------------------------------- //

/** Albero delle cartelle di un obbligo. Lettura pubblica, come i contenuti. */
export function leggiCartelle(
  idNodo: IdNodo,
  enteId?: number | null,
  segnale?: AbortSignal,
): Promise<Cartella[]> {
  return richiesta<Cartella[]>(
    "GET",
    `${PREFISSO_V1}/nodi/${encodeURIComponent(idNodo)}/cartelle`,
    { query: { ente: enteId ?? undefined }, segnale },
  );
}

/**
 * Scrive, cambia o toglie (`null` o testo vuoto) la nota pubblica sull'assenza
 * di contenuti di un obbligo. E' visibile sul portale da subito.
 */
export function impostaNotaAssenza(
  idNodo: IdNodo,
  testo: string | null,
  enteId?: number | null,
): Promise<Nodo> {
  return richiesta<Nodo>(
    "PUT",
    `${PREFISSO_V1}/nodi/${encodeURIComponent(idNodo)}/nota-assenza`,
    { corpo: { testo }, query: { ente: enteId ?? undefined } },
  );
}

/** Crea una cartella dentro un obbligo, eventualmente annidata. */
export function creaCartella(
  idNodo: IdNodo,
  dati: RichiestaCreazioneCartella,
  enteId?: number | null,
): Promise<Cartella> {
  return richiesta<Cartella>(
    "POST",
    `${PREFISSO_V1}/nodi/${encodeURIComponent(idNodo)}/cartelle`,
    { corpo: dati, query: { ente: enteId ?? undefined } },
  );
}

/**
 * Rinomina, riordina o sposta una cartella.
 *
 * `id_padre` si distingue **per presenza**: ometterlo lascia la cartella dov'e',
 * passarlo a `null` la riporta in radice. Chi chiama deve quindi costruire il
 * corpo con il campo assente, non valorizzato a `undefined` dentro un oggetto
 * che poi viene serializzato — `JSON.stringify` scarta gli `undefined`, quindi
 * le due forme coincidono, ma la distinzione va tenuta a mente leggendo.
 */
export function aggiornaCartella(
  idCartella: number,
  dati: RichiestaAggiornamentoCartella,
): Promise<Cartella> {
  return richiesta<Cartella>("PUT", `${PREFISSO_V1}/cartelle/${idCartella}`, {
    corpo: dati,
  });
}

/**
 * Elimina una cartella.
 *
 * I documenti **non** vengono cancellati: tornano in radice dell'obbligo. Senza
 * `ricorsiva` l'API risponde 409 `CARTELLA_NON_VUOTA` su una cartella che
 * contiene qualcosa, cosi' che il redattore veda che cosa sta per spostare.
 */
export function eliminaCartella(idCartella: number, ricorsiva = false): Promise<void> {
  return richiesta<void>("DELETE", `${PREFISSO_V1}/cartelle/${idCartella}`, {
    query: ricorsiva ? { ricorsiva: true } : undefined,
  });
}

/** Sposta un documento in una cartella, o in radice se `cartellaId` e' `null`. */
export function spostaDocumento(
  idDocumento: number,
  cartellaId: number | null,
): Promise<Documento> {
  return richiesta<Documento>("PUT", `${PREFISSO_V1}/documenti/${idDocumento}/cartella`, {
    corpo: { cartella_id: cartellaId },
  });
}

/**
 * Carica un documento direttamente su un obbligo.
 *
 * E' la via di caricamento del back-office: il Comune ha dei file e li mette
 * online. Il documento finisce comunque in una bozza e passa comunque dalla
 * validazione del contenuto — la brevita' riguarda la compilazione, non i
 * controlli — e diventa pubblico solo con `pubblica`, che si comanda
 * dall'alberatura.
 */
export function caricaDocumentoSuNodo(
  idNodo: IdNodo,
  file: File,
  titolo: string,
  opzioni: {
    cartellaId?: number | null;
    anno?: number;
    enteId?: number | null;
    /** Durata di pubblicazione in anni; assente vale quella dell'obbligo. */
    durataAnni?: number | null;
    /** Ultimo giorno di pubblicazione, gg/mm/aaaa, in alternativa alla durata. */
    finoAl?: DataIt | null;
    /** Scadenza del file, gg/mm/aaaa: poi il file va sostituito, ma resta pubblicato. */
    validoFinoAl?: DataIt | null;
    /**
     * Altri obblighi in cui pubblicare lo stesso file: una copia per obbligo,
     * con nome, indirizzo e durata di quell'obbligo. Tutto o niente.
     */
    copie?: RichiestaCopia[];
  } = {},
): Promise<Documento> {
  const modulo = new FormData();
  modulo.append("file", file);
  modulo.append("titolo", titolo);
  if (opzioni.cartellaId != null) modulo.append("cartella_id", String(opzioni.cartellaId));
  if (opzioni.anno != null) modulo.append("anno", String(opzioni.anno));
  if (opzioni.durataAnni != null) modulo.append("durata_anni", String(opzioni.durataAnni));
  if (opzioni.finoAl) modulo.append("fino_al", opzioni.finoAl);
  if (opzioni.validoFinoAl) modulo.append("valido_fino_al", opzioni.validoFinoAl);
  // JSON e non campi ripetuti: il multipart non annida, e ogni copia porta la
  // propria durata accanto al proprio obbligo.
  if (opzioni.copie?.length) modulo.append("copie", JSON.stringify(opzioni.copie));
  return richiesta<Documento>(
    "POST",
    `${PREFISSO_V1}/nodi/${encodeURIComponent(idNodo)}/documenti`,
    { modulo, query: { ente: opzioni.enteId ?? undefined } },
  );
}

// --------------------------------------------------------------------------- //
// Accessibilita' dei documenti
// --------------------------------------------------------------------------- //

/** Filtri di `GET /api/v1/documenti`. */
export interface FiltriDocumenti {
  q?: string;
  formato?: FormatoDocumento[];
  accessibilita?: StatoAccessibilita[];
  nodo?: IdNodo;
  includiBozze?: boolean;
  includiArchiviate?: boolean;
  limite?: number;
  scarto?: number;
  ente?: number | null;
}

/**
 * Elenca i documenti del perimetro, filtrabili per accessibilita'.
 *
 * Una richiesta sola, non una per obbligo: i documenti da correggere sono
 * sparsi fra nodi diversi, ed e' precisamente la ragione per cui questo elenco
 * esiste accanto alla pagina di gestione, che ne mostra un obbligo per volta.
 */
export function elencaDocumenti(
  filtri: FiltriDocumenti = {},
  segnale?: AbortSignal,
): Promise<ElencoDocumenti> {
  return richiesta<ElencoDocumenti>("GET", `${PREFISSO_V1}/documenti`, {
    query: {
      q: filtri.q || undefined,
      formato: filtri.formato?.length ? filtri.formato : undefined,
      accessibilita: filtri.accessibilita?.length ? filtri.accessibilita : undefined,
      nodo: filtri.nodo,
      includi_bozze: filtri.includiBozze ? true : undefined,
      includi_archiviate: filtri.includiArchiviate ? true : undefined,
      limite: filtri.limite,
      scarto: filtri.scarto,
      ente: filtri.ente ?? undefined,
    },
    segnale,
  });
}

/**
 * Cambia la durata di pubblicazione di un documento, anche gia' pubblicato.
 *
 * Non tocca il file ne' alcun indirizzo: cambia solo il giorno in cui il
 * documento uscira' dalla pubblicazione corrente. Entrambi nulli riportano il
 * documento alla durata dell'obbligo.
 */
export function impostaDurataDocumento(
  idDocumento: number,
  durata: { durata_anni: number | null; fino_al: DataIt | null },
): Promise<Documento> {
  return richiesta<Documento>("PUT", `${PREFISSO_V1}/documenti/${idDocumento}/durata`, {
    corpo: durata,
  });
}

/**
 * Imposta o toglie (`null`) la scadenza del file.
 *
 * Vale per tutte le righe dello **stesso file**: la corrente, il clone nella
 * revisione aperta e le copie collegate negli altri obblighi, che sono lo
 * stesso file. Non per una correzione in attesa, che e' un file nuovo con una
 * scadenza sua. Non tocca la pubblicazione: passata la data il file resta
 * online, e chiede un sostituto.
 */
export function impostaScadenzaDocumento(
  idDocumento: number,
  validoFinoAl: DataIt | null,
): Promise<Documento> {
  return richiesta<Documento>("PUT", `${PREFISSO_V1}/documenti/${idDocumento}/scadenza`, {
    corpo: { valido_fino_al: validoFinoAl },
  });
}

/**
 * Porta un documento nell'archivio della pagina dell'obbligo.
 *
 * **Non e' una cancellazione**: il file resta ai suoi indirizzi. Se nella
 * pubblicazione non resta altro di corrente, l'API archivia anche quella.
 */
export function archiviaDocumento(idDocumento: number): Promise<Documento> {
  return richiesta<Documento>("POST", `${PREFISSO_V1}/documenti/${idDocumento}/archivia`);
}

/**
 * Riesegue i controlli su un documento e ne aggiorna il verdetto.
 *
 * Lecita anche su un documento gia' pubblicato, per la stessa ragione per cui
 * lo e' spostarlo fra cartelle: non cambia un solo indirizzo pubblico. Con
 * veraPDF attivo puo' impiegare qualche secondo — e' un processo Java che
 * parte — quindi chi la invoca deve mostrare che sta lavorando.
 */
export function riverificaDocumento(idDocumento: number): Promise<Documento> {
  return richiesta<Documento>(
    "POST",
    `${PREFISSO_V1}/documenti/${idDocumento}/riverifica`,
  );
}

/**
 * Mette un file nuovo al posto di un documento, in bozza.
 *
 * Il titolo non si invia: resta quello del documento sostituito, ed e' cio' che
 * alla pubblicazione sposta l'alias `-latest` sul file nuovo. Il vecchio resta
 * al suo indirizzo versionato. Il sostituto diventa pubblico con `pubblica`,
 * come ogni altro caricamento.
 */
export function sostituisciDocumento(
  idDocumento: number,
  file: File,
  opzioni: {
    /** Sostituisce anche le copie collegate negli altri obblighi: tutte o nessuna. */
    ancheCopie?: boolean;
    /**
     * Scadenza del file nuovo. Quella del vecchio non passa mai al sostituto,
     * qualunque sia il motivo della sostituzione: se il file nuovo deve averne
     * una, va indicata qui (le modali di sostituzione la chiedono).
     */
    validoFinoAl?: DataIt | null;
  } = {},
): Promise<Documento> {
  const modulo = new FormData();
  modulo.append("file", file);
  if (opzioni.ancheCopie) modulo.append("anche_copie", "true");
  if (opzioni.validoFinoAl) modulo.append("valido_fino_al", opzioni.validoFinoAl);
  return richiesta<Documento>(
    "POST",
    `${PREFISSO_V1}/documenti/${idDocumento}/sostituzione`,
    { modulo },
  );
}

// --------------------------------------------------------------------------- //
// Assistente alla trasparenza
// --------------------------------------------------------------------------- //

/** Vero se l'installazione ha l'assistente acceso: decide se montare il launcher. */
export function statoAssistente(segnale?: AbortSignal): Promise<StatoAssistente> {
  return richiesta<StatoAssistente>("GET", `${PREFISSO_V1}/assistente/stato`, { segnale });
}

/** Saluto e domande suggerite per un nodo: risponde dalla scheda, senza modello. */
export function aperturaAssistente(
  idNodo: IdNodo,
  enteId?: number | null,
  segnale?: AbortSignal,
): Promise<AperturaAssistente> {
  return richiesta<AperturaAssistente>("GET", `${PREFISSO_V1}/assistente/nodi/${encodeURIComponent(idNodo)}/apertura`, {
    query: { ente: enteId ?? undefined },
    segnale,
  });
}

/**
 * Il WAF di Bunny Shield davanti al sito ispeziona il corpo e a volte scambia per
 * un attacco una risposta del modello rimandata come contesto. Il suo 403 si
 * riconosce perche' Bunny lo da' da se', senza interrogare l'origine: manca
 * `CDN-RequestPullSuccess`, che c'e' su ogni risposta arrivata dal nostro server.
 */
function bloccoDelFiltro(risposta: Response): boolean {
  return (
    risposta.status === 403 &&
    (risposta.headers.get("Server") ?? "").startsWith("BunnyCDN") &&
    !risposta.headers.has("CDN-RequestPullSuccess")
  );
}

/** Richiesta fermata dal filtro di sicurezza del sito, prima di arrivare all'API. */
export class ErroreFiltro extends ErroreApi {
  /** L'identificativo di Bunny (`CDN-RequestId`) con cui ritrovare il blocco nei suoi log. */
  readonly riferimento: string | null;

  constructor(riferimento: string | null) {
    const coda = riferimento ? ` Riferimento: ${riferimento}.` : "";
    super("BLOCCATO_DAL_FILTRO", `Il filtro di sicurezza del sito ha bloccato la richiesta.${coda}`, 403);
    this.name = "ErroreFiltro";
    this.riferimento = riferimento;
  }
}

/**
 * Apre lo stream della chat e restituisce la `Response` grezza: il corpo e'
 * `text/event-stream` e lo legge `leggiEventiSse`. Non passa da `richiesta`
 * perche' quella consuma il corpo come JSON e impone un timeout complessivo che
 * uno stream di qualche decina di secondi supererebbe. Un rifiuto prima dello
 * stream (401, 404, 422, 503) arriva come JSON ed e' un `ErroreApi` normale.
 */
export async function streamAssistente(
  corpo: RichiestaChatAssistente,
  segnale: AbortSignal,
): Promise<Response> {
  const intestazioni = new Headers({ Accept: "text/event-stream", "Content-Type": "application/json" });
  const token = tokenCorrente();
  if (token) intestazioni.set("Authorization", `Bearer ${token}`);
  let risposta: Response;
  try {
    risposta = await fetch(componiUrl(`${PREFISSO_V1}/assistente/chat`), {
      method: "POST",
      headers: intestazioni,
      body: JSON.stringify(corpo),
      signal: segnale,
      credentials: "omit",
      cache: "no-store",
    });
  } catch (errore) {
    if (segnale.aborted) throw errore;
    throw new ErroreApi("ERRORE_INTERNO", MESSAGGI_RETE.irraggiungibile, 0);
  }
  if (risposta.status === 401) {
    chiudiSessione();
    throw await erroreDaRisposta(risposta);
  }
  if (bloccoDelFiltro(risposta)) {
    const riferimento = risposta.headers.get("CDN-RequestId");
    throw new ErroreFiltro(riferimento);
  }
  if (!risposta.ok) throw await erroreDaRisposta(risposta);
  return risposta;
}
